import React, { FC, useRef, useState } from 'react';
import HLPageTable, { HLPageTableRowActionButton } from '../../../shared/components/table/HLPageTable';
import { ColDef } from 'ag-grid-community';
import { getAllLessonsFetch, updateLessonStructureByIdFetch } from '../../../app/axios/lessonFetches';
import { useParams } from 'react-router-dom';
import { Lesson, LessonType, LessonTypeNameMap } from '../../../shared/types/lesson';
import { formatDateTimeForString } from '../../../shared/utils/dateUtils';
import { PageRequest } from "../../../shared/types/page";
import UserCreateLessonsModal from '../../../modal/lesson/UserCreateLessonsModal';
import classes from "../../student/lessons/StudentLessonsCard.module.scss";
import { AgreementStructure } from "../../../shared/types/agreement";
import { useAppSelector } from "../../../shared/hooks/useAppSelector";
import { TeacherRole } from "../../../shared/types/teacher";
import AdminCreateLessonsModal from "../../../modal/lesson/AdminCreateLessonsModal";
import DeleteLessonModal from "../../../modal/lesson/DeleteLessonModal";

const columnDefs: ColDef<Lesson>[] = [
    {
        field: 'lessonDate',
        headerName: 'Дата урока',
        valueFormatter: (params) => params.value && formatDateTimeForString(params.value)
    },
    {
        field: 'type',
        headerName: 'Тип урока',
        valueFormatter: (params) => LessonTypeNameMap[params.value as LessonType]
    },
    {
        field: 'teacherRate',
        headerName: 'Ставка учителя',
        valueFormatter: (params) => params.value && `${params.value} ₽`
    },
    {
        field: 'conducted',
        headerName: 'Проведен',
        valueFormatter: params => params.value ? 'Да' : 'Нет'
    },
];

const defaultColDef: ColDef<Lesson> = {
    sortable: false,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 100,
};

interface AgreementLessonsCardProps {
    agreement: AgreementStructure;
    loadAgreement: () => void;
}

const AgreementLessonsCard: FC<AgreementLessonsCardProps> = (
    {
        agreement,
        loadAgreement
    }
) => {
    const {id: agreementId} = useParams();
    const {currentTeacher} = useAppSelector(state => state.auth);
    const tableRef = useRef<any>(null);
    const [isCreateLessonModalOpen, setIsCreateLessonModalOpen] = useState(false);
    const [isDeleteLessonModalOpen, setIsDeleteLessonModalOpen] = useState(false);
    const [lessonId, setLessonId] = useState(null)

    const fetchData = async (pageRequest: PageRequest) => {
        const request = {
            ...pageRequest,
            sort: {orderBy: 'lessonDate', orderType: 'desc' as const},
            filters: [
                ...(pageRequest.filters || []),
                {fieldName: 'agreementId', operator: '=', fieldValue: agreementId}
            ]
        };
        return getAllLessonsFetch(request);
    };

    const handleSuccess = () => {
        setIsDeleteLessonModalOpen(false);
        setLessonId(null)
        setIsCreateLessonModalOpen(false);
        loadAgreement();
        tableRef.current?.refreshData();
    };

    const actionButtons = [
        {
            label: currentTeacher?.role === TeacherRole.ADMIN ? 'Создать урок' : 'Создать уроки',
            onClick: () => setIsCreateLessonModalOpen(true),
        }
    ];

    const rowActionButtons: HLPageTableRowActionButton[] = [
        {
            onClick: async params => {
                if (!params?.data?.id) return

                const conducted = params?.data?.conducted
                try {
                    await updateLessonStructureByIdFetch(params.data?.id, {
                        conducted: !conducted,
                        students: agreement.students.map(student => ({
                            studentId: student.id,
                            paid: !conducted,
                            attended: !conducted,
                        }))
                    });

                    loadAgreement();
                    params.api.refreshInfiniteCache();
                } catch (err) {
                    console.error('Ошибка при смене признака "Проведен" у урока:', err);
                }
            },
            getLabel: params => params?.data?.conducted ? 'Не проведен' : 'Проведен'
        },
        {
            onClick: async params => {
                if (!params?.data?.id) return
                setLessonId(params.data.id)
                setIsDeleteLessonModalOpen(true)
            },
            getLabel: () => 'Удалить'
        }
    ];

    const handleOpenLesson = (params: { data?: Lesson }) => {
        if (params?.data?.id) window.open(`/agreements/${agreementId}/lessons/${params.data.id}`, '_blank', 'noreferrer');
    };

    return (
        <>
            <div className={classes.contentContainer}>
                <HLPageTable
                    ref={tableRef}
                    title="Уроки по договору"
                    columns={columnDefs}
                    defaultColDef={defaultColDef}
                    filterConfig={[]}
                    actionButtons={actionButtons}
                    rowActionButtons={rowActionButtons}
                    rowActionButtonsMinWidth={200}
                    onRowDoubleClick={handleOpenLesson}
                    fetchData={fetchData}
                    height={485}
                />
            </div>
            {
                currentTeacher?.role === TeacherRole.ADMIN
                    ? <AdminCreateLessonsModal
                        open={isCreateLessonModalOpen}
                        setOpen={setIsCreateLessonModalOpen}
                        agreement={agreement}
                        onSuccess={handleSuccess}
                    />
                    : <UserCreateLessonsModal
                        open={isCreateLessonModalOpen}
                        setOpen={setIsCreateLessonModalOpen}
                        agreementId={agreementId}
                        onSuccess={handleSuccess}
                    />
            }
            <DeleteLessonModal
                open={isDeleteLessonModalOpen}
                setOpen={setIsDeleteLessonModalOpen}
                lessonId={lessonId}
                onSuccess={handleSuccess}/>
        </>
    );
};

export default AgreementLessonsCard;